import uuidv4 from 'uuid/v4'
import jwtDecode from 'jwt-decode'
import moment from 'moment'
import request from 'request-promise'
import { get, isNil } from 'lodash'
import omitDeep from 'omit-deep-lodash'
import { Constants } from '@sukiai/components'
import { isCharUnsupported } from '@sukiai/utils'
import loSto from '../../config/loSto'
import seshSto from '../../config/seshSto'
import Auth from '../../config/okta'
import {
  ROUTES,
  LO_STO,
  SESH_STO,
  HALF_STORY,
  INTERCOM_EVENTS,
  ORG_IDS
} from '../../lib'
import { logger, halfStory as hs, logIntercomEvent } from '../../lib/logging'
import { DataCommand } from '../../ws/DataCommand'
import { stopRecording } from '../../containers/Agent/Recorder'
import {
  AutoDeleteComposition,
  GetAllNotes,
  GetAppointments,
  GetUnfinishedCompositions,
  GetUnfinishedCompositionsCount,
  GetUserById,
  GetNotePDF,
  GetOrganizationsById,
  GetScriptsByUserId,
  GetCompositionById,
  UpdateComposition,
  SearchDiagnosesML,
  GetAllNoteTypes,
  GetNotesByUserId
} from '@sukiai/gql/app'
import { RecordNoteEvent } from '@sukiai/gql/common'
import client from '../../apollo'
import {
  SET_MODAL,
  GET_SCHEDULE_DATE,
  GET_AGENT_STATE,
  SET_AGENT_STATE,
  SET_AGENT_POSITION,
  SET_AGENT_SIZE,
  SET_TOAST,
  SET_SECTION,
  SET_DICTATION,
  SET_AUDIO_STREAM_UI_CONTEXT,
  CLEAR_SECTION,
  GET_CURRENT_COMPOSITION,
  SET_CURRENT_COMPOSITION,
  GET_CURRENT_NOTETYPE_ID,
  SET_NOTE_CREATED_PENDING,
  GET_COMP_BEING_CREATED,
  SET_MODAL_DISMISS
} from '../../apollo/actions'
import { getStreamWorker, sendSpeechConfig } from '../../ws/stream'
import { getIntercomID, BASE_URL } from '../../config'
import {
  FETCH_POLICY,
  MODAL_STRINGS,
  SUBMISSION_WARNING,
  CLIENT_TYPE,
  GQL_STATUS,
  SCRIPT_TAG,
  DISAMBIG,
  SETTINGS_VIEW,
  SECONDARY_EMR_TYPE,
  EMR_DESTINATION,
  EMR_TYPE,
  EMR_INTEGRATION_STATUS
} from '../constants'
import { AudioEvent } from '../../audio/AudioEvent'
import { VIDEO_FILES } from '../../containers/IntroTutorial/constants'
import { THEMES } from '../../core/SukiContext'

// Lodash
import _isNil from 'lodash/isNil'
import _isEmpty from 'lodash/isEmpty'

import sukiUserReader from '../../readers/sukiUser'
import sukiOrganizationReader from '../../readers/sukiOrganization'
import { fetchFhirConfig } from '../../services/fhirJson'
import pubSub from './pubSub'
import { EMR_INTEGRATION_DETAILS_UPDATE } from '../../constants/pubSub.events'
import { getFhirTokensFromCache } from '../../helpers/fhirEmrTokens'

const { AGENT, MODALS, SECTION_STATE, TOAST } = Constants

export const inNoteView = () => window.location.pathname.split('/')[1] === 'note'

export const isPathInNoteView = pathname => pathname.split('/')[1] === 'note'

export const inScheduleView = () => window.location.pathname.split('/')[1] === 'schedule'

export const inPatientView = () => window.location.pathname.split('/')[1] === 'patient'

export const isEmrUser = () => {
  const emrType = get(seshSto.session(SESH_STO.CURRENT_ORGANIZATION), 'emr.type')
  return !!emrType
}

export const isEmrConnectedUser = () => {
  if (isAthena()) {
    // LDA-90
    return true
  }
  const { emrIntegrated, status } = loSto.get(LO_STO.EMR_INTEGRATION_DETAILS) || {}
  return emrIntegrated && status === EMR_INTEGRATION_STATUS.CONNECTED
}

export const isAthena = () => {
  return get(seshSto.session(SESH_STO.CURRENT_ORGANIZATION), 'emr.secondaryType') === 'ATHENA_EMR'
}

export const isCerner = () => {
  return get(seshSto.session(SESH_STO.CURRENT_ORGANIZATION), 'emr.secondaryType') === 'CERNER_EMR'
}

export const isElation = () => {
  return get(seshSto.session(SESH_STO.CURRENT_ORGANIZATION), 'emr.secondaryType') === 'ELATION_EMR'
}

export const isEpic = () => {
  return get(seshSto.session(SESH_STO.CURRENT_ORGANIZATION), 'emr.secondaryType') === 'EPIC_EMR'
}

export const isRedRover = () => {
  return get(seshSto.session(SESH_STO.CURRENT_ORGANIZATION), 'emr.configurations.is_red_rover_org') === '1'
}

export const isSubmitNotesAsPreliminary = () => {
  return get(seshSto.session(SESH_STO.CURRENT_ORGANIZATION), 'emr.configurations.submit_notes_as_preliminary') === '1'
}

export const isSukiOnly = () => {
  const destinations = loSto.get(LO_STO.SUBMISSION_DESTINATIONS)
  const destination = destinations && destinations.length > 0 && destinations[0]
  return destination && destination === EMR_DESTINATION.SUKI_ONLY
}

export const isInIframe = () => {
  return window.location !== window.parent.location
}

export const isValidPathname = pathname => {
  // Need more robust valid pathname detection
  // wont detect incorrect id's and the such
  const prefix = '/' + pathname.split('/')[1]
  return Object.values(ROUTES).indexOf(prefix) > -1
}

export const isWindowsUpdatePathname = pathname => {
  const prefix = '/' + pathname.split('/')[1]
  return Object.values(ROUTES).indexOf(prefix) > -1
}

const clearStateAndRedirectToLogin = () => {
  loSto.clearAll()
  window.localStorage.clear()
  window.location.href = '/'
}

export const logout = () => {
  seshSto.session(SESH_STO.AGENT_RECORDING) && stopRecording()
  Auth.logout()
  const submitPreference = loSto.get(LO_STO.SUBMIT_PREFERENCE)
  const gettingStartedTaskDetails = loSto.get(LO_STO.OOBE_TASKS)
  const isFirstTimeUser = loSto.get(LO_STO.IS_FIRST_TIME_USER)
  loSto.clearAll()
  window.localStorage.clear()

  // preserve DISABLE_AGENT from session storage
  const disableAgent = seshSto.session(SESH_STO.DISABLE_AGENT)
  window.sessionStorage.clear()
  seshSto.session(SESH_STO.DISABLE_AGENT, disableAgent)

  loSto.set(LO_STO.SUBMIT_PREFERENCE, submitPreference)
  loSto.set(LO_STO.OOBE_TASKS, gettingStartedTaskDetails)
  loSto.set(LO_STO.IS_FIRST_TIME_USER, isFirstTimeUser)
  loSto.set(LO_STO.LOGOUT_EVENT, true)
  window.location.href = '/'
}

// Generates a 64-bit identifier
export const generateSuki64bitID = () => {
  return uuidv4().replace(/-/g, '').substring(16)
}

// play an audio file
export const play = sound => {
  const { SOUNDS } = Constants
  const audioTag = window.Audio && new window.Audio(sound)

  if (sound === SOUNDS.ERROR) {
    audioTag.volume = 0.2
  } else if (sound === SOUNDS.END_OF_UTTERANCE) {
    audioTag.volume = 0.4
  }

  const promise = audioTag.play()

  if (promise !== undefined) {
    promise.then(_ => {
      // Autoplay started!
    }).catch(error => {
      console.error('Unable to play sound', error)
    })
  }
}

// Given a pathname, return a RoutePath to compare
// eg. /note/8d9205df-db0f-402d-ba6b-16bd3f4e7f44 => /note
export const stripIds = path => {
  const pathArray = path.split('/')
  const len = pathArray.length
  return len > 1 ? `/${pathArray[1]}` : path
}

// Sort an array of objects by key
export const sortItemsByKey = (arr, key) => {
  arr.sort((a, b) => {
    return a[key].toLowerCase() < b[key].toLowerCase() ? -1
      : a[key].toLowerCase() > b[key].toLowerCase() ? 1
        : 0
  })
  return arr
}

// Sort newest to oldest
export const timeSortDesc = (arr, key) => {
  arr.sort((a, b) => {
    a = new Date(a[key])
    b = new Date(b[key])
    return b > a
      ? 1
      : b < a
        ? -1
        : 0
  })
  return arr
}

// Sort oldest to newest
export const timeSortAsc = (arr, key) => {
  arr.sort((a, b) => {
    a = new Date(a[key])
    b = new Date(b[key])
    return a > b
      ? 1
      : a < b
        ? -1
        : 0
  })
  return arr
}

export const setError = err => {
  const { MODALS } = Constants
  client.mutate({
    mutation: SET_MODAL,
    variables: {
      content: {
        __typename: 'ModalContent',
        type: MODALS.ERROR,
        heading: 'Oops!',
        body: err
      }
    }
  })
}

export const deleteProblemSection = async (sectionId) => {
  hs.add(HALF_STORY.CLICK, `Deleting Problem Section [${sectionId}]`)
  const { composition } = await client.readQuery({
    query: GET_CURRENT_COMPOSITION
  })
  const comp = JSON.parse(composition, null, 2)
  const parentSection = getPbcParentSectionS2(comp.sectionsS2)[0]
  const notetypeId = await client.query({
    query: GET_CURRENT_NOTETYPE_ID
  }).then((data) => data.data.currentNotetypeId)
  const sectionData = comp.sectionsS2.find(section => section.id === sectionId)

  const stream = getStreamWorker()
  stream && stream.postMessage({
    cmd: 'USER_REQUEST',
    userRequestType: 'DELETE_SECTION',
    userRequestData: {
      compositionId: comp.id,
      notetypeId: notetypeId,
      sectionId: parentSection?.id,
      sectionData
    },
    ts: new Date().toISOString()
  })

  sectionId && deletePbcSectionInCache(comp.id, sectionId)
}

export const getAllScriptsForUser = async () => {
  try {
    const variables = { userId: loSto.get(LO_STO.USER_ID) }
    const res = await client.query({
      query: GetScriptsByUserId,
      variables,
      fetchPolicy: FETCH_POLICY.NETWORK_ONLY
    })
    return res?.data?.macros.results
  } catch (err) {
    console.error('Couldn\'t get scripts for user')
  }
}

export const getAllNotetypesForUser = async () => {
  try {
    const variables = {
      organizationId: loSto.get(LO_STO.ORGANIZATION_ID),
      userId: loSto.get(LO_STO.USER_ID)
    }
    const res = await client.query({
      query: GetAllNoteTypes,
      variables,
      fetchPolicy: FETCH_POLICY.NETWORK_ONLY
    })
    return res?.data?.noteTypes.results
  } catch (err) {
    console.error('Couldn\'t get all notetypes for user')
    return []
  }
}

export const showSettings = async (selectType) => {
  client.mutate({
    mutation: SET_MODAL,
    variables: {
      content: {
        __typename: 'ModalContent',
        type: MODALS.SETTINGS,
        selectType: selectType
      }
    }
  })
}

export const promptICD10Lookup = (id, name, fromConfirm, sectionContent) => {
  const items = [{
    __typename: 'ModalItem',
    id,
    name
  }]

  client.mutate({
    mutation: SET_MODAL,
    variables: {
      content: {
        __typename: 'ModalContent',
        type: MODALS.ICD10_SEARCH,
        items,
        data: {
          __typename: 'ModalData',
          fromConfirm: fromConfirm,
          sectionContent: sectionContent
        }
      }
    }
  })
}

export const showNotetypes = async () => {
  // Retrieve all notetypes for the curent user
  const variables = { userId: loSto.get(LO_STO.USER_ID) }
  const res = await client.query({
    query: GetAllNoteTypes,
    variables,
    fetchPolicy: FETCH_POLICY.NETWORK_ONLY
  })

  const noteTypes = res.data.noteTypes.results

  // Format scripts for modal
  const items = noteTypes.map(s => ({
    __typename: 'ModalItem',
    id: s.id,
    name: s.name
  }))

  client.mutate({
    mutation: SET_MODAL,
    variables: {
      content: {
        __typename: 'ModalContent',
        type: MODALS.SELECT,
        selectType: 'NOTETYPES',
        heading: 'Notetypes',
        items
      }
    }
  })
}

export const showScripts = async () => {
  try {
    // Retrieve all scripts for the curent user
    const variables = { userId: loSto.get(LO_STO.USER_ID) }
    const res = await client.query({
      query: GetScriptsByUserId,
      variables,
      fetchPolicy: FETCH_POLICY.NETWORK_ONLY
    })
    const scripts = res.data.macros.results

    // Set agent position to left of modal
    if (!inNoteView()) {
      client.mutate({
        mutation: SET_AGENT_POSITION,
        variables: { agentPosition: AGENT.POSITION.LEFT }
      })
    }

    // Format scripts for modal
    const items = scripts.map(s => ({
      __typename: 'ModalItem',
      id: s.id,
      name: getScriptName(s)
    }))

    // Set scripts modal
    client.mutate({
      mutation: SET_MODAL,
      variables: {
        content: {
          __typename: 'ModalContent',
          type: MODALS.SELECT,
          selectType: DISAMBIG.SCRIPTS,
          heading: 'Scripts',
          items
        }
      }
    })

    logIntercomEvent(INTERCOM_EVENTS.SHOW_SCRIPTS)
  } catch (err) {
    console.error('Could not open scripts modal', err)
  }
}

const initializeLocalStorage = (sukiUser, sukiOrganization, notesCount) => {
  loSto.set(LO_STO.USER_ID, sukiUserReader.id(sukiUser))
  loSto.set(LO_STO.ORGANIZATION_ID, sukiOrganizationReader.id(sukiOrganization))
  loSto.set(LO_STO.CURRENT_USER, sukiUser)
  loSto.set(LO_STO.SCHEDULE_DATE, moment().format())
  loSto.set(LO_STO.SETTINGS_VIEW, SETTINGS_VIEW.SCRIPTS)

  const isFirstTimeUserFromLocalStore = loSto.get(LO_STO.IS_FIRST_TIME_USER)
  if (_isNil(isFirstTimeUserFromLocalStore) && notesCount === 0 && !isAmwellTheme()) {
    loSto.set(LO_STO.IS_FIRST_TIME_USER, true)
  } else {
    loSto.set(LO_STO.IS_FIRST_TIME_USER, false)
  }

  // Check if first time amwell user
  if (
    notesCount <= 1 && isAmwellTheme()
  ) {
    loSto.set(LO_STO.IS_AMWELL_FIRST_TIME_USER, true)
  } else {
    loSto.set(LO_STO.IS_AMWELL_FIRST_TIME_USER, false)
  }
}

const initializeSessionStorage = (sukiUser, sukiOrganization) => {
  seshSto.session(SESH_STO.USER_ID, sukiUserReader.id(sukiUser))
  seshSto.session(SESH_STO.CURRENT_ORGANIZATION, sukiOrganization)
}

const initializeEMRIntegrationDetails = async () => {
  const fhirConfig = await fetchFhirConfig()
  const emrIntegrationDetails = ('error' in fhirConfig) ? {
    emrIntegrated: false
  } : {
    ...fhirConfig,
    emrIntegrated: true,
    status: isNil(loSto.get(LO_STO.FHIR_ACCESS_TOKEN)) ? EMR_INTEGRATION_STATUS.PENDING : EMR_INTEGRATION_STATUS.CONNECTED,
    additional: {
      displayEmrIntegrationAlert: isNil(loSto.get(LO_STO.FHIR_ACCESS_TOKEN))
    }
  }
  loSto.set(LO_STO.EMR_INTEGRATION_DETAILS, emrIntegrationDetails)
  pubSub.publish(EMR_INTEGRATION_DETAILS_UPDATE)
  if (emrIntegrationDetails.emrIntegrated) {
    getFhirTokensFromCache()
  }
}

export const initializeUser = async (auth) => {
  const jwt = await auth.getIdToken()
  const decodedJwt = jwtDecode(jwt)
  const { userID: userId, organizationID: organizationId, email, roles, name, created_at: createdAt } = decodedJwt

  loSto.set(LO_STO.ORGANIZATION_ID, organizationId)

  // Make sure the JWT contains a user ID, org ID, and roles
  if (!userId || !organizationId || !roles || !email) {
    console.error('User info is missing in the JWT.')
    clearStateAndRedirectToLogin()
    return
  }

  // Make sure the user has the right role
  if (!roles.includes('USER')) {
    console.error('User is not authorized for this page.')
    clearStateAndRedirectToLogin()
    return
  }

  // Query for the suki user and organization objects
  const promises = [
    client.query({
      query: GetUserById,
      variables: { id: userId, onLogin: true }
    }),
    client.query({
      query: GetOrganizationsById,
      variables: { id: organizationId }
    }),
    client.query({
      query: GetNotesByUserId,
      variables: { userId: userId }
    })
  ]

  try {
    const [userResponse, organizationResponse, notesResponse] = await Promise.all(promises)

    const sukiUser = get(userResponse, 'data.users.results[0]')
    const sukiOrganization = get(organizationResponse, 'data.organizations.results[0]')
    const notesCount = get(notesResponse, 'data.allNotes.count')

    initializeSessionStorage(sukiUser, sukiOrganization)
    initializeLocalStorage(sukiUser, sukiOrganization, notesCount)
    initializeEMRIntegrationDetails()

    // Start the websocket stream worker
    const stream = getStreamWorker()

    // ALSO TURN OFF BADGE TO KICK OFF ONBOARDING
    stream.postMessage({
      event: AudioEvent.MUTE_AGENT,
      payload: true
    })

    // await retrieveFeatureFlags(jwtUser, ldSdkId)
  } catch (err) {
    console.error('Error in getting User/or Organization: ', err)
  }

  // Intercom
  // List of data we want to track
  window.intercomSettings = {
    app_id: getIntercomID(),
    user_id: userId, // User ID
    name: name, // Full name
    email: email, // Email address
    created_at: createdAt, // Signup Date
    emr: seshSto.session(SESH_STO.CURRENT_ORGANIZATION)?.emr?.type,
    'Org id': organizationId
  }
}

// Get status page components (for reporting in help center)
export const getStatusPageComponents = async () => {
  const jwt = await Auth.getIdToken()

  const options = {
    url: `${BASE_URL}/statuspage/components`,
    headers: {
      Accept: 'application/json',
      Authorization: jwt
    },
    resolveWithFullResponse: true
  }

  try {
    const res = await request(options)
    const body = JSON.parse(res.body)
    return body
  } catch (err) {
    console.error('Error in retrieving statuspage components:', err)
  }
}

// Attempt to set current appointment for patient,
// given all appointments for the day set on the schedule
export const setCurrentAppointment = async patientId => {
  const { scheduleDate } = client.readQuery({ query: GET_SCHEDULE_DATE })

  return client.query({
    query: GetAppointments,
    variables: {
      ownerId: loSto.get(LO_STO.USER_ID),
      startsAtRangeBeginning: moment(scheduleDate.date).startOf('day').toISOString(),
      startsAtRangeEnd: moment(scheduleDate.date).endOf('day').toISOString()
    }
  }).then(({ data }) => {
    const allAppts = data && data.appointments && data.appointments.results
    // TODO: this might not work for all cases..
    // IF patient has two visits in a day, and doctor wants to create an appointment for the 2nd one
    // this logic will break
    const selectedAppt = allAppts.find(a => a.patient && a.patient.id === patientId)
    if (selectedAppt) {
      const apptId = selectedAppt.id
      loSto.set(LO_STO.CURRENT_APPT_ID, apptId)
      return apptId
    }
  })
}

export const autoDeleteNote = async compId => {
  const scheduleDate = moment(loSto.get(LO_STO.SCHEDULE_DATE))
  return client.mutate({
    mutation: AutoDeleteComposition,
    // userId: loSto.get(LO_STO.USER_ID), organizationId: loSto.get(LO_STO.ORGANIZATION_ID),
    variables: { id: compId },
    refetchQueries: [
      {
        query: GetAppointments,
        variables: {
          ownerId: loSto.get(LO_STO.USER_ID),
          startsAtRangeBeginning: scheduleDate.startOf('day').toISOString(),
          startsAtRangeEnd: scheduleDate.endOf('day').toISOString()
        }
      },
      {
        query: GetUnfinishedCompositionsCount,
        variables: { userId: loSto.get(LO_STO.USER_ID) }
      },
      {
        query: GetUnfinishedCompositions,
        variables: { userId: loSto.get(LO_STO.USER_ID) }
      },
      {
        query: GetAllNotes,
        variables: {
          userId: loSto.get(LO_STO.USER_ID),
          patientId: loSto.get(LO_STO.CURRENT_PATIENT_ID)
        }
      }
    ]
  })
}

export const deleteNote = async compositionId => {
  // eventually use this for all those in place cache updates.. oof that's gonna be annoying
  // const scheduleDate = moment(loSto.get(LO_STO.SCHEDULE_DATE))

  const stream = getStreamWorker()

  stream && stream.postMessage({
    cmd: DataCommand.DELETE_COMPOSITION,
    organizationId: loSto.get(LO_STO.ORGANIZATION_ID),
    compositionId,
    userId: loSto.get(LO_STO.USER_ID),
    ts: new Date().toISOString()
  })
}

export const createUpdateAttachOrCreatePatientRequest = ({ requestType, patient, compositionId, notetypeId }) => {
  const stream = getStreamWorker()

  stream && stream.postMessage({
    cmd: DataCommand.USER_REQUEST,
    userRequestType: requestType,
    userRequestData: {
      compositionId,
      notetypeId,
      patient,
      patientId: patient.id
    },
    ts: new Date().toISOString()
  })
}

// This funcion is used to `updateAppointment` for non-EMR users. We can only change the DoS
export const createUpdateAppointmentRequest = ({ appointment, compositionId, notetypeId }) => {
  const stream = getStreamWorker()

  stream && stream.postMessage({
    cmd: DataCommand.USER_REQUEST,
    userRequestType: 'UPDATE_APPOINTMENT',
    userRequestData: {
      compositionId,
      notetypeId,
      appointment
    },
    ts: new Date().toISOString()
  })
}

export const scrollToSection = (id, goToTop) => {
  if (isAmwellTheme()) {
    return
  }
  // Select element and bounding rectangle in DOM
  const el = id && document.getElementById(id)
  const elRect = el && el.getBoundingClientRect()

  // If the desired element does not yet exist on the screen
  // return without scrolling
  if (!elRect) return

  let pos = ''

  // Section is larger than window
  if (elRect.height > window.innerHeight) {
    pos = goToTop ? 'start' : 'end'
    // Section bottom is out of view
  } else if (!goToTop && elRect.bottom > window.innerHeight) {
    pos = 'end'
    // Section can fit in the window
  } else {
    pos = 'center'
  }

  el && el.scrollIntoView({
    behavior: 'smooth',
    block: pos
  })
}

export const scrollToSidebarSection = (id) => {
  const sectionName = 'section-container-' + id
  const element = id && document.getElementById(sectionName)

  element && element.scrollIntoView({
    behavior: 'smooth',
    block: 'center'
  })
}

export const getGender = person => (
  person && person.gender && person.gender !== 'UNKNOWN'
    ? person.gender[0] + person.gender.substring(1).toLowerCase()
    : null
)

export const getAge = person => {
  if (person && person.dateOfBirth) {
    if (new Date(person.dateOfBirth).getTime() === 0) return '--'

    const ageYears = moment().diff(person.dateOfBirth, 'years')
    const ageMonths = moment().diff(person.dateOfBirth, 'months')
    return ageYears >= 2
      ? ageYears
      : ageMonths >= 1 && `${ageMonths} ${ageMonths === 1 ? 'month' : 'months'}`
  }
  return null
}

export const getAgeAndGender = person => {
  const age = getAge(person)
  const gender = getGender(person)

  return `${age ? `Age ${age}` : ''}${age && gender ? '  •  ' : ''}${gender || ''}`
}

function downloadPDFtoChromeBar (pdfUrl) {
  fetch(pdfUrl).then(resp => resp.arrayBuffer()).then(resp => {
    // set the blog type to final pdf
    const file = new Blob([resp], { type: 'application/pdf' })

    // process to auto download it
    const fileURL = URL.createObjectURL(file)
    const link = document.createElement('a')
    link.href = fileURL
    link.download = 'Suki-' + new Date() + '.pdf'
    link.click()
  })
}

export const recordNoteEvent = async (event, data) => {
  const noteEventData = {
    ...data,
    timestamp: new Date().toISOString(),
    clientType: CLIENT_TYPE.WEB
  }

  return client.mutate({
    mutation: RecordNoteEvent,
    variables: {
      noteEvent: event,
      noteEventData: noteEventData
    }
  }).then((res, event) => {
    logger.info('Record Note Event Successful', event)
    logger.debug('Result of Record Note Event:', res)
    console.info('Record Note Event Successful', event)
  }).catch(err => {
    console.error('Error Recording Event Note:', err)
  })
}

export const printCurrentNote = () => {
  // Only one of these should ever be set at a time
  const compositionId = loSto.get(LO_STO.CURRENT_COMP_ID)
  const noteId = loSto.get(LO_STO.CURRENT_NOTE_ID)

  client.query({
    query: GetNotePDF,
    variables: {
      compositionId,
      noteId
    },
    fetchPolicy: FETCH_POLICY.NETWORK_ONLY
  }).then(({ data }) => {
    const uri = get(data, 'notePDF.pdf.uri')
    if (uri) {
      window.open(uri, '_blank')
    } else {
      setError('Could not print note. Please try again later.')
    }
  }).catch(err => {
    const gqlError = get(err, 'graphQLErrors[0].status')
    if (gqlError === GQL_STATUS.FAILED_PRECONDITION) {
      setError("Suki can't print notes with unprocessed dictation. Please try again later.")
      return
    }
    console.error('Could not print note:', err)
    setError('Could not print note. Please try again later.')
  })
}

export const emailCurrentNote = () => {
  // Only one of these should ever be set at a time
  const compositionId = loSto.get(LO_STO.CURRENT_COMP_ID)
  const noteId = loSto.get(LO_STO.CURRENT_NOTE_ID)

  setModal(
    MODALS.LOADING,
    'Generating PDF',
    ''
  )

  client.query({
    query: GetNotePDF,
    variables: {
      compositionId,
      noteId
    },
    fetchPolicy: FETCH_POLICY.NETWORK_ONLY
  }).then(({ data }) => {
    client.mutate({ mutation: SET_MODAL_DISMISS })

    const uri = get(data, 'notePDF.pdf.uri')
    const isCerner = true

    if (!uri) {
      setError('Could not print note. Please try again later.')
      return
    }

    if (isCerner) {
      const mailToLink = 'mailto:?subject=Your%20Suki%20Note&body=Powered%20by%20Suki'
      window.open(mailToLink, '_blank')

      downloadPDFtoChromeBar(uri)
    } else {
      window.open(uri, '_blank')
    }
  }).catch(err => {
    client.mutate({ mutation: SET_MODAL_DISMISS })

    const gqlError = get(err, 'graphQLErrors[0].status')
    if (gqlError === GQL_STATUS.FAILED_PRECONDITION) {
      setError("Suki can't print notes with unprocessed dictation. Please try again later.")
      return
    }
    console.error('Could not print note:', err)
    setError('Could not print note. Please try again later.')
  })
}

export const setAgentState = state => {
  const { agentState: oldState } = client.readQuery({ query: GET_AGENT_STATE })

  if (oldState !== Constants.AGENT.STATE.MUTED) {
    client.mutate({
      mutation: SET_AGENT_STATE,
      variables: { agentState: state }
    })
  }
}

export const setModal = (type, heading, body) => (
  client.mutate({
    mutation: SET_MODAL,
    variables: {
      content: { __typename: 'ModalContent', type, heading, body }
    }
  })
)

// Sign-off modal for non-EMR users
export const setSignOffModal = () => {
  const { MODALS } = Constants

  return setModal(
    MODALS.SIGN_OFF,
    MODAL_STRINGS.SIGN_OFF_HEADING,
    MODAL_STRINGS.SIGN_OFF_BODY
  )
}

export const doesCompositionHaveProblemSections = comp => {
  const sections = get(comp, 'sectionsS2', [])
  return sections.some(s => !!s.diagnosisEntry)
}

export const cancelNoteSubmission = (comp) => {
  const stream = getStreamWorker()

  const userRequestData = {
    organizationId: comp.organizationId,
    noteTypeId: comp?.metadata?.noteTypeId,
    compositionId: comp.id,
    patientId: comp?.metadata?.patient?.id,
    appointmentId: comp?.metadata?.appointment?.id
  }

  logger.info('[S2 ALERT - SENDING CANCEL_NOTE_SUBMISSION EVENT]')
  stream && stream.postMessage({
    cmd: DataCommand.USER_REQUEST,
    userRequestType: 'CANCEL_NOTE_SUBMISSION',
    userRequestData,
    ts: new Date().toISOString()
  })
}

export const sendInitialNoteSubmission = (comp) => {
  const stream = getStreamWorker()

  const userRequestData = {
    organizationId: comp.organizationId,
    noteTypeId: comp?.metadata?.noteTypeId,
    compositionId: comp.id,
    patientId: comp?.metadata?.patient?.id,
    appointmentId: comp?.metadata?.appointment?.id
  }

  stream && stream.postMessage({
    cmd: DataCommand.USER_REQUEST,
    userRequestType: 'INITIAL_NOTE_SUBMISSION',
    userRequestData,
    ts: new Date().toISOString()
  })
}

// Sets the different modal props depending on the current note state when I'm done is clicked or I'm done
// is initiated via voice.
export const setSubmitModal = async (compId) => {
  const { MODALS } = Constants

  const comp = await getCompFromCache(compId)
  /*   if (doesCompHaveContentError(comp)) {
    return setModal(
      MODALS.CONTENT_ERROR
    )
  } */

  // do check here
  if (!loSto.get(LO_STO.CURRENT_PATIENT_ID)) {
    // No patient has been selected by doctor
    // This modal should pop up immediately, before any other submission options
    return setModal(
      MODALS.UNKNOWN_PATIENT_SAVE,
      MODAL_STRINGS.UNKNOWN_PATIENT_HEADING,
      MODAL_STRINGS.UNKNOWN_PATIENT_BODY
    )
  }

  if (doesCompositionHaveProblemSections(comp)) {
    return setModal(
      MODALS.CONFIRM_CODES
    )
  }

  sendInitialNoteSubmission(comp)

  const submissionInfo = comp?.metadata?.submissionInformation?.destinations

  const heading = submissionInfo?.[0] === EMR_DESTINATION.DOCUMENT_NEEDS_SIGNOFF
    ? MODAL_STRINGS.IM_DONE_DOC_SIGNOFF
    : MODAL_STRINGS.IM_DONE_BYPASS_QA_HEADING

  return setModal(
    MODALS.SUBMIT_OPTIONS,
    heading,
    MODAL_STRINGS.IM_DONE_BYPASS_QA_BODY
  )
}

export const startCreateNote = async () => {
  if (await getCompBeingCreated()) return
  setCompBeingCreated(true)

  client.mutate({
    mutation: SET_NOTE_CREATED_PENDING,
    variables: {
      isPending: true
    }
  })

  const stream = getStreamWorker()
  stream && stream.postMessage({
    cmd: DataCommand.USER_REQUEST,
    userRequestType: 'CREATE_NOTE',
    userRequestData: {},
    ts: new Date().toISOString()
  })
}

export const cancelCreateNote = (compId) => {
  const stream = getStreamWorker()
  const userRequestData = {
    compositionId: compId
  }

  logger.info('[S2 ALERT - SENDING CANCEL_CREATE_NOTE EVENT]')
  stream && stream.postMessage({
    cmd: DataCommand.USER_REQUEST,
    userRequestType: 'CANCEL_CREATE_NOTE',
    userRequestData,
    ts: new Date().toISOString()
  })

  loSto.remove(LO_STO.CURRENT_COMP_ID)
  loSto.set(LO_STO.FROM_PATIENT_LIST, false)

  setCompBeingCreated(false)
}

// These are warning modals that appear after the decision the submission method has been selected.
export const setSubmissionWarningModal = () => {
  const { MODALS } = Constants

  const warning = loSto.get(LO_STO.SUBMISSION_WARNING)
  const [primaryDestination, secondaryDestination] = loSto.get(
    LO_STO.SUBMISSION_DESTINATIONS
  )

  switch (warning) {
    // No encounter is open in EMR for this appointment
    case SUBMISSION_WARNING.NO_ENCOUNTER:
    case SUBMISSION_WARNING.ENCOUNTER_NOT_OPEN:

      if (primaryDestination !== EMR_DESTINATION.DOCUMENT &&
          secondaryDestination === EMR_DESTINATION.DOCUMENT) {
        return setModal(
          MODALS.PDF_SUBMIT,
          MODAL_STRINGS.PDF_SUBMIT_HEADING,
          MODAL_STRINGS.PDF_SUBMIT_BODY
        )
      } else {
        return setModal(
          MODALS.NO_ENCOUNTER,
          MODAL_STRINGS.NO_ENCOUNTER_HEADING,
          MODAL_STRINGS.NO_ENCOUNTER_BODY
        )
      }

    // Patient is not found in EMR
    case SUBMISSION_WARNING.UNKNOWN_PATIENT:
      return setModal(
        MODALS.UNKNOWN_PATIENT_SAVE,
        MODAL_STRINGS.UNKNOWN_PATIENT_EMR_HEADING,
        MODAL_STRINGS.UNKNOWN_PATIENT_EMR_BODY
      )
    // No lab results found in EMR
    case SUBMISSION_WARNING.NO_LAB_RESULTS:
      return setModal(
        'DEFAULT',
        MODAL_STRINGS.NO_LAB_RESULTS_HEADING,
        loSto.get(LO_STO.SUBMISSION_WARNING_MSG)
      )
    // No valid OB episode found in EMR
    case SUBMISSION_WARNING.NO_VALID_OB_EPISODE:
      return setModal(
        'DEFAULT',
        MODAL_STRINGS.NO_OB_FLOWSHEET_HEADING,
        loSto.get(LO_STO.SUBMISSION_WARNING_MSG)
      )
    default:
  }
}

export const handleNoteDeletion = (currentCompId) => {
  autoDeleteNote(currentCompId)
    .then(() => {
      logger.info(`Empty note deleted: ${currentCompId}`)
      hs.add(HALF_STORY.NOTE_DELETED, `Deleted note ${currentCompId}`)
      console.info('Deleted Note')
    }).catch(err => {
      console.error('Error deleting note:', err)
    })
}

export const getScriptName = script => {
  if (!script) return ''
  const tags = get(script, 'tags', [])
  const nameTag = tags.find(t => t.type === SCRIPT_TAG.NAME)
  return get(nameTag, 'name.value', '')
}

export const setToast = (type, message, duration) => {
  client.mutate({
    mutation: SET_TOAST,
    variables: { type, message, duration }
  })
}

// getCompFromCache retrieves the composition from the apollo cache if it has been queried via
// the GetCompositionById query
export const getCompFromCache = async compId => {
  let comp = null
  try {
    const compInCache = await client.query({
      query: GET_CURRENT_COMPOSITION
    })
    const compStr = compInCache?.data?.composition
    comp = JSON.parse(compStr)
  } catch (err) {
    console.error('Error retrieving', err)
  }

  return comp
}

export const getCompFromCacheSync = compId => {
  try {
    const { composition } = client.readQuery({
      query: GET_CURRENT_COMPOSITION
    })
    return JSON.parse(composition)
  } catch (err) {
    console.error('Unable to retrieve comp from cache synchronously')
    return null
  }
}

// getCompSectionFromCache retrieves the composition
// then filters and return the section that matches sectionId
export const getCompSectionFromCache = async (compId, sectionId) => {
  const comp = await getCompFromCache(compId)
  let section = null
  try {
    const sectionIdx = comp.sectionsS2.findIndex(
      section => section.id === sectionId
    )
    section = comp.sectionsS2[sectionIdx]
  } catch (err) {
    console.error('Unable to retrieve section from apollo cache:', err)
  }

  return section
}

// write sectionData to cache
export const updateSectionInCache = async (compId, sectionData) => {
  try {
    const noteData = await getCompFromCache(compId)
    const sectionIdx = noteData.sectionsS2.findIndex(
      section => section.id === sectionData.id
    )
    const oldSection = { ...noteData.sectionsS2[sectionIdx] }
    noteData.sectionsS2[sectionIdx] = {
      ...oldSection,
      ...sectionData,
      content_s2: JSON.parse(sectionData.content)
    }
    client.mutate({
      mutation: SET_CURRENT_COMPOSITION,
      variables: {
        composition: JSON.stringify(noteData)
      }
    })
  } catch (e) {
    console.error('Could not write note data to cache')
  }
}

export const addPbcSectionInCache = async (compId, pbcSectionData) => {
  try {
    const compInCache = await getCompFromCache(compId)
    const sections = get(compInCache, 'sectionsS2')

    // this line does not work if there are no problems to begin with
    // Find the index into which we'd like to insert the new problem section
    let idxToInsert
    const doesCompHavePbcSections = sections.findIndex(s => !!s.diagnosisEntry) > 0

    if (doesCompHavePbcSections) {
      idxToInsert = sections.length - sections.slice().reverse().findIndex(s => !!s.diagnosisEntry)
    } else {
      idxToInsert = sections.find(s => !!s.pbcSectionFlag).sectionIndex
    }

    const sectionToAdd = pbcSectionData
    sectionToAdd.__typename = 'CompositionSection'
    sectionToAdd.diagnosisEntry.__typename = 'DiagnosisEntry'
    sections.splice(idxToInsert, 0, sectionToAdd)
    compInCache.sectionsS2 = sections

    loSto.set(LO_STO.CURRENT_COMPOSITION, compInCache)

    client.mutate({
      mutation: SET_CURRENT_COMPOSITION,
      variables: {
        composition: JSON.stringify(compInCache)
      },
      update: (cache) => {
        cache.writeQuery({
          query: GetCompositionById,
          variables: { id: compInCache.id },
          data: compInCache
        })
      }
    })
  } catch (err) {
    console.error('Could not update composition in cache', err)
  }
}

export const deletePbcSectionInCache = async (compId, sectionId) => {
  try {
    const compInCache = await getCompFromCache(compId)
    let sections = get(compInCache, 'sectionsS2')

    // go to section where pbc is located
    // find and destroy deleted section
    // renumber the problmes

    const pbcSectionIdx = sections.findIndex(s => s.id === sectionId)
    sections.splice(pbcSectionIdx, 1)

    let pbcIndexCount = 0
    sections = sections.map((section) => {
      if (section.diagnosisEntry) {
        pbcIndexCount++
        return {
          ...section, subsectionIndex: pbcIndexCount
        }
      }

      return section
    })

    // Find the index into which we'd like to insert the new problem section
    // const idxToInsert = sections.length - sections.slice().reverse().findIndex(s => !!s.diagnosisEntry)

    // const sectionToAdd = pbcSectionData
    // sectionToAdd.__typename = 'CompositionSection'
    // sectionToAdd.diagnosisEntry.__typename = 'DiagnosisEntry'
    // sections.splice(idxToInsert, 0, sectionToAdd)
    compInCache.sectionsS2 = sections

    loSto.set(LO_STO.CURRENT_COMPOSITION, compInCache)

    client.mutate({
      mutation: SET_CURRENT_COMPOSITION,
      variables: {
        composition: JSON.stringify(compInCache)
      },
      update: (cache) => {
        cache.writeQuery({
          query: GetCompositionById,
          variables: { id: compInCache.id },
          data: compInCache
        })
      }
    })
  } catch (err) {
    console.error('Could not update composition in cache', err)
  }
}

export const updatePbcSectionInCache = async (compId, pbcSectionData) => {
  try {
    let compInCache = await getCompFromCache(compId)
    let sections = get(compInCache, 'sectionsS2')
    // find section idx
    let sectionIdx = sections.findIndex(s => s.id === pbcSectionData.id)

    // next code needed, to be sure that cache exist new data before update it
    let dataUpdatingAttempts = 3
    while (sectionIdx === -1 && dataUpdatingAttempts > 0) {
      compInCache = await getCompFromCache(compId)
      sections = get(compInCache, 'sectionsS2')
      sectionIdx = sections.findIndex(s => s.id === pbcSectionData.id)
      dataUpdatingAttempts--
    }

    sections[sectionIdx] = { ...sections[sectionIdx], ...pbcSectionData }
    compInCache.sectionsS2 = sections

    loSto.set(LO_STO.CURRENT_COMPOSITION, compInCache)

    await client.mutate({
      mutation: SET_CURRENT_COMPOSITION,
      variables: {
        composition: JSON.stringify(compInCache)
      },
      update: (cache) => {
        cache.writeQuery({
          query: GetCompositionById,
          variables: { id: compInCache.id },
          data: compInCache
        })
      }
    })

    client.mutate({
      mutation: SET_AUDIO_STREAM_UI_CONTEXT,
      variables: {
        audioStreamUIContext: {
          problemBasedSections: await getPbSectionCtx(compInCache.id)
        }
      }
    })
  } catch (err) {
    console.error('Could not update composition in cache', err)
  }
}

// getProblemSections takes an array of sections in a composition, and returns only
// the problem sections.
export const getProblemSections = sections => {
  return sections.filter(s => !!s.diagnosisEntry).map(s => omitDeep(s, '__typename'))
}

// check is some of sections is marked as NEEDS_OPS_PROCESSING
export const isProblemSectionNeedOpsProcessing = () => {
  const composition = loSto.get(LO_STO?.CURRENT_COMPOSITION) || []
  const sections = composition?.sectionsS2

  let isOpsProcessingRequired = false

  if (_.isArray(sections) && sections.length !== 0) {
    sections.map(section => {
      if (section?.opsStatusFlag === 'NEEDS_OPS_PROCESSING') isOpsProcessingRequired = true
    })
  }

  return isOpsProcessingRequired
}

// Gets the pbcParentSection
export const getPbcParentSectionS2 = sections => {
  // we need to validate to make sure there is only one pbc section per notetype in the backend
  // this will/should always return only one pbc section
  return sections.filter(s => !!s.pbcSectionFlag).map(s => omitDeep(s, '__typename'))
}

// getPbSectionCtx takes a composition ID and returns
// a list of section ids and names so that voiceagent is aware of a notes problem based sections.
export const getPbSectionCtx = async compId => {
  const sbCtx = []

  const comp = await getCompFromCache(compId)
  if (!comp) return sbCtx
  const pSections = getProblemSections(comp.sectionsS2)
  return pSections.map(s => ({ idNamePair: `${s.id}:${s.name}` }))
}

export const setSectionDictation = async (compId, sectionId) => {
  if (!seshSto.session(SESH_STO.AGENT_RECORDING)) {
    return
  }
  client.mutate({
    mutation: SET_AUDIO_STREAM_UI_CONTEXT,
    variables: {
      audioStreamUIContext: {
        agentAwake: false,
        transcription: true,
        sectionId: sectionId,
        isScriptChange: false,
        patientId: '',
        problemBasedSections: await getPbSectionCtx(compId)
      }
    }
  }).then(() => {
    sendSpeechConfig('Sending new speech configuration because sectionId changed.')

    scrollToSection(sectionId)

    client.mutate({
      mutation: SET_SECTION,
      variables: { id: sectionId, state: SECTION_STATE.DICTATION }
    })

    setAgentState(AGENT.STATE.LISTENING)

    client.mutate({
      mutation: SET_AGENT_SIZE,
      variables: { agentSize: AGENT.SIZE.SMALL }
    })

    client.mutate({
      mutation: SET_AGENT_POSITION,
      variables: { agentPosition: AGENT.POSITION.TOP_LEFT }
    })

    client.mutate({
      mutation: SET_DICTATION,
      variables: { dictation: true }
    })
  })
}

// resets section dictation mode
export const stopSectionDictation = (agentState, shouldClearSection = true, currentSectionId) => {
  client.mutate({
    mutation: SET_DICTATION,
    variables: { dictation: false }
  })

  if (shouldClearSection) {
    client.mutate({
      mutation: CLEAR_SECTION,
      variables: { dictation: true }
    })
    // also unfocus sectioneditor
    const sectionEditorEl = document.getElementById(`sectionEditor-${currentSectionId}`)
    if (sectionEditorEl) {
      sectionEditorEl.blur()
      logger.info(`Unfocused section ${currentSectionId} after STOP DICTATION [DOM ID=${sectionEditorEl.id}]`)
    }
  }

  agentState !== AGENT.STATE.MUTED && setAgentState(AGENT.STATE.READY)

  client.mutate({
    mutation: SET_AGENT_SIZE,
    variables: { agentPosition: AGENT.SIZE.SMALL }
  })

  client.mutate({
    mutation: SET_AGENT_POSITION,
    variables: { agentPosition: AGENT.POSITION.TOP_LEFT }
  })
}
// findDuplicates takes an array and returns
// a set of all duplicated values
export const findDuplicates = (arr) => {
  const unique = new Set()
  const dupes = new Set()

  for (const ele of arr) {
    if (unique.has(ele)) {
      dupes.add(ele)
    } else {
      unique.add(ele)
    }
  }

  return dupes
}

export const updateSection = (compositionId, section) => {
  try {
    const compInCache = client.readQuery({
      query: GetCompositionById,
      variables: { id: compositionId }
    })

    const noteData = get(compInCache, 'compositions.results[0]')

    const sectionIdx = noteData.sections.findIndex(
      _section => _section.id === section.id
    )

    noteData.sections[sectionIdx] = section

    client.writeQuery({
      query: GetCompositionById,
      variables: { id: compositionId },
      data: noteData
    })
  } catch (err) {
    console.error('Could not find appointments in cache', err)
  }

  client.mutate({
    mutation: UpdateComposition,
    variables: {
      id: compositionId,
      sections: [
        section
      ]
    }
  })
}

// Validate change modes (keyboard/audio) and events related
// const validateChangeEventState = keyboardOrAudioEvent => {

// client.query(GET_S2_EVENTS_ARRAY, )

// // first event being sent
// if sentS2EventArray.isEmpty && event != .audioEnd { return true }
// guard let lastEvent = sentS2EventArray.last else { return true }
// if lastEvent == event { return false }
// // now let's get schwifty with the rules here
// // if the last event is audio start, a keybaord on/off event should never be allowed
// if lastEvent == .audioStart {
//     if event == .keyboardOn || event == .keyboardOff {
//         return false
//     } else {
//         return true
//     }
//     // if the last event was keyboard on, the only valid event is keyboard off
// } else if lastEvent == .keyboardOn {
//     if event == .keyboardOff {
//         return true
//     } else {
//         return false
//     }
//     // if the last event was keyboard off, then it is impossible to send audio end
// } else if lastEvent == .keyboardOff {
//     if event == .audioEnd {
//         return false
//     } else {
//         return true
//     }
//     // if the last evebt was audio end, it is impossible to send keyboard off
// } else if lastEvent == .audioEnd {
//     if event == .keyboardOff {
//         return false
//     } else {
//         return true
//     }
// }
// return true
// }

// This is the one that gets called when you click on the add problem
export const addProblemSection = async (compositionId) => {
  // Sending a user request to S2 instead
  // https://github.com/LearningMotors/suki/blob/9e4d05d51e0ede801f9658d060a80d58cecccbdf/ios/suki/gRPC/Dialog/DialogRequestHandler.swift#L268
  const { composition } = await client.readQuery({
    query: GET_CURRENT_COMPOSITION
  })
  const comp = JSON.parse(composition, null, 2)
  const parentSection = getPbcParentSectionS2(comp.sectionsS2)[0]
  const notetypeId = await client.query({
    query: GET_CURRENT_NOTETYPE_ID
  }).then((data) => data.data.currentNotetypeId)

  const stream = getStreamWorker()

  setTimeout(() => {
    stream && stream.postMessage({
      cmd: 'USER_REQUEST',
      userRequestType: 'ADD_SECTION',
      userRequestData: {
        compositionId,
        notetypeId: notetypeId,
        sectionId: parentSection?.id
      },
      ts: new Date().toISOString()
    })
  })
}

export const autoPopulateICD = (compositionID, section) => {
  return client.query({
    query: SearchDiagnosesML,
    variables: {
      searchTitle: section.name,
      searchDescription: section.name
    }
  })
    .then(({ data }) => {
      const results = get(data, 'searchDiagnosesML.diagnoses', [])
      const hasOneResult = results.length === 1
      const isLeaf = results[0] && results[0].children && results[0].children.length === 0
      if (hasOneResult && isLeaf) {
        const suggestedDiagnosis = results[0].diagnosis

        section.diagnosisEntry = {
          diagnosis: {
            ...suggestedDiagnosis
          },
          confirmStatus: 'UNCONFIRMED'
        }

        const cleanSection = omitDeep(section, '__typename')
        updateSection(compositionID, cleanSection)
      }
    })
}

export const autoPopulateICD10AfterAdd = async (section) => {
  if (!section.name) return null
  return client.query({
    query: SearchDiagnosesML,
    variables: {
      searchTitle: section.name,
      searchDescription: section.name
    }
  })
    .then(async ({ data }) => {
      const results = get(data, 'searchDiagnosesML.diagnoses', [])
      const hasOneResult = results.length === 1
      const isLeaf = results[0] && results[0].children && results[0].children.length === 0
      if (hasOneResult && isLeaf) {
        const suggestedDiagnosis = results[0].diagnosis

        section.diagnosisEntry = {
          diagnosis: {
            ...suggestedDiagnosis,
            problemLabel: section.name
          },
          confirmStatus: 'UNCONFIRMED'
        }
        section.updateType = 'ONLY_DIAGNOSIS'

        const cleanSection = omitDeep(section, '__typename')
        updateDiaognistics(cleanSection)
      }

      return null
    })
}

export const formatCode = code => {
  if (!code) return ''
  if (code.length > 3) {
    return code.substring(0, 3) + '.' + code.substring(3)
  }
  return code
}

export const openProcessingNote = id => {
  loSto.set(LO_STO.CURRENT_COMP_ID, id)
  loSto.remove(LO_STO.CURRENT_NOTE_ID)

  // display warning
  setToast(TOAST.TYPE.PRIMARY, 'Suki is currently processing this note. It will be available for your review in a few minutes.', TOAST.DURATION.MEDIUM)

  // set agent position to left
  client.mutate({
    mutation: SET_AGENT_POSITION,
    variables: { agentPosition: AGENT.POSITION.LEFT }
  })

  // set agent size to large
  client.mutate({
    mutation: SET_AGENT_SIZE,
    variables: { agentSize: AGENT.SIZE.LARGE }
  })

  // set modal
  client.mutate({
    mutation: SET_MODAL,
    variables: {
      content: {
        __typename: 'ModalContent',
        type: MODALS.READ_NOTE,
        data: {
          isProcessing: true
        }
      }
    }
  })
}

export const updateDiaognistics = async (cachedSection) => {
  const compositionId = loSto.get(LO_STO.CURRENT_COMP_ID)
  // const notetypeId = compositionData?.metadata?.noteTypeId
  const notetypeId = await client.query({
    query: GET_CURRENT_NOTETYPE_ID
  }).then((data) => data.data.currentNotetypeId)
  // send s2
  const stream = getStreamWorker()

  stream && stream.postMessage({
    cmd: 'USER_REQUEST',
    userRequestType: 'UPDATE_SECTION',
    userRequestData: {
      compositionId,
      sectionData: cachedSection,
      sectionId: cachedSection?.id,
      notetypeId
    },
    ts: new Date().toISOString()
  })
  await updatePbcSectionInCache(compositionId, cachedSection)
}

export const getExistingDiagnosis = (sections) => {
  const filtered = sections.filter(s => !!s.diagnosisEntry && !!s.diagnosisEntry.diagnosis.icdCode)
  return filtered.map(s => ({
    snomedDescription: s.name,
    icdCode: s.diagnosisEntry.diagnosis.icdCode,
    icdDescription: s.diagnosisEntry.diagnosis.description,
    snomedCode: s.diagnosisEntry.diagnosis.snomedCode
  }))
}

export const doesCompHaveContentError = (comp) => {
  const sections = get(comp, 'sectionsS2', [])
  return sections.some((section) => isContentError(section))
}

export const isContentError = (section) => {
  const plainText = get(section, 'plainText', '')
  return plainText.split('').some(char => {
    return isCharUnsupported(char)
  })
}

export const refreshUnfinishedNoteCount = () => {
  const userId = loSto.get(LO_STO.USER_ID)

  client.query({
    query: GetUnfinishedCompositionsCount,
    variables: {
      userId
    },
    fetchPolicy: FETCH_POLICY.NETWORK_ONLY
  })
}

// Sorts script objects by name
export const sortScriptsByName = scripts =>
  scripts.sort((a, b) => {
    const aTag = a.tags.find(t => t.type === SCRIPT_TAG.NAME)
    const bTag = b.tags.find(t => t.type === SCRIPT_TAG.NAME)

    if (!bTag) return -1
    if (!aTag) return 1

    const aName = get(aTag, 'name.value', '').toLowerCase()
    const bName = get(bTag, 'name.value', '').toLowerCase()

    return aName < bName ? -1 : aName > bName ? 1 : 0
  })

export const generateDestinations = () => {
  const secondaryType = get(seshSto.session(SESH_STO.CURRENT_ORGANIZATION), 'emr.secondaryType')
  const isEncounter = isRedRover() || isSubmitNotesAsPreliminary()
  switch (secondaryType) {
    case SECONDARY_EMR_TYPE.ATHENA_EMR:
    case SECONDARY_EMR_TYPE.ELATION_EMR:
      return [EMR_DESTINATION.ENCOUNTER, EMR_DESTINATION.DOCUMENT]
    case SECONDARY_EMR_TYPE.CERNER_EMR:
    case SECONDARY_EMR_TYPE.EPIC_EMR:
      if (isEncounter) {
        return [EMR_DESTINATION.ENCOUNTER]
      }
      return [EMR_DESTINATION.ENCOUNTER_NEEDS_SIGNOFF]
    default:
      return [EMR_DESTINATION.SUKI_ONLY]
  }
}

export const setCompBeingCreated = (compBeingCreated) => {
  return client.writeQuery({
    query: GET_COMP_BEING_CREATED,
    data: { compBeingCreated }
  })
}

export const getCompBeingCreated = () => {
  return client.readQuery({ query: GET_COMP_BEING_CREATED })?.compBeingCreated
}

export const getOobeFile = () => {
  const emr = seshSto.session(SESH_STO.CURRENT_ORGANIZATION)?.emr

  if (!emr?.id) {
    /// does not belong to emr/
    return VIDEO_FILES.OOBE
  } else {
    if (emr?.type === EMR_TYPE.ATHENA) {
      return VIDEO_FILES.OOBE_ATHENA
    } else {
      return VIDEO_FILES.OOBE_EPIC_CERNER
    }
  }
}

export const getMacroContent = (id) => {
  const stream = getStreamWorker()
  stream && stream.postMessage({
    cmd: 'USER_REQUEST',
    userRequestType: 'GET_MACRO_CONTENT',
    userRequestData: {
      scriptId: id
    },
    ts: new Date().toISOString()
  })
}

export const isAmwellTheme = () => loSto.get('themeMode') === THEMES.DARK

export const getMicState = () => loSto.get('micEnabled')

export const isEmbedScriptsView = (route) => route === ROUTES.SCRIPTS && isAmwellTheme()

export const isNoteView = () => {
  return window.location.pathname.includes(`${ROUTES.NOTE}/`)
}

export const isAgentDisabled = () => {
  // TODO: Disabled temporarily for Amwell Alpha release
  // const micEnabled = loSto.get('micEnabled')
  // return !_isNil(micEnabled) && !micEnabled
  return isEmbedScriptsView() || false
}

/**
 * Checks if the organization of the logged in user has any EMR integration
 * Or If it is a End to End test Organization
 * @returns {Boolean} Returns True if EMR integrated, else false.
 */
export const isOrgEmrIntegrated = () => {
  const organizationId = seshSto.session(SESH_STO.CURRENT_ORGANIZATION)?.id
  const emrId = seshSto.session(SESH_STO.CURRENT_ORGANIZATION)?.emr?.id
  const isEmrIntegrated = !_isEmpty(emrId) || organizationId === ORG_IDS.E2E
  return isEmrIntegrated
}

/**
 * Get expiry time in seconds by decoding
 * jwt token
 */
export const getTokenExpiryInSeconds = (jwt) => {
  const { exp } = jwtDecode(jwt)
  return moment.unix(exp).diff(moment(), 'seconds')
}

export const isAmwellVisitScreen = () => isAmwellTheme() && (loSto.get('pageType') === 'visit')
